import ImageGallery from '../components/ImageGallery';
import React, { useMemo } from 'react';
import Icon from '../../common/components/Icon';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import ProductsBlock from '../components/ProductsBlock';
import { ProductCardProps } from '../../home/types/types';
import Breadcrumbs from '../../components/breadcrumb/Breadcrumb';
import Chip from '../../components/chip/Chip';
import getDomainFromUrl from '../../utils/domainFromUrl';
import { useTranslations } from '../../localization/useTranslations';
import GoogleEvent from '../../utils/gaEvent';
import useViewItemEvent from '../../hooks/useViewItemEvent';
import useClickEvent from '../../hooks/useClickEvent';
import { formatPrice } from '../../common/Formatter';

export interface DetailPageProps extends ProductCardProps {
  recommendedProducts?: ProductCardProps[];
}

const DetailPage: React.FC<DetailPageProps> = ({
  id,
  images,
  title,
  permalink,
  content,
  url,
  urlDomain: url_domain,
  priceAmountNum: price_amount,
  priceCurrency: price_currency,
  attributes = [],
  brand,
  vendor,
  recommendedProducts,
  labels,
  categories,
}) => {
  const { isExtraSmall, isSmall, isMedium } = useBreakpoint();
  const isMobile = isExtraSmall || isSmall || isMedium;
  const translations = useTranslations();

  const renderAttributes = () => {
    return attributes?.map((attribute: any, index: number) => (
      <div key={index} className="ecom-detail__attribute">
        <h5 className="ecom-detail__attribute-name">{attribute.name}</h5>
        <p className="ecom-detail__attribute-value basic-16">{attribute.value}</p>
      </div>
    ));
  };

  const breadcrumbs = useMemo(() => {
    const root = {
      label: translations?.ecommerce?.shopping || 'Shopping',
      path: '/market/',
    };

    const product = {
      label: title,
      path: permalink,
    };

    return [root, product];
  }, [translations?.ecommerce?.shopping, title, permalink]);

  const formattedPrice = useMemo(
    () => formatPrice(Number(price_amount), price_currency),
    [price_amount, price_currency]
  );

  useViewItemEvent(id, title, url_domain, brand?.name, categories, price_currency, price_amount);

  const handleGoToClick = useClickEvent('go_to_vendor', () => {
    const itemCategories = GoogleEvent.generateItemCategories(categories ?? []);

    return {
      items: [
        {
          referrer: window.location.href,
          item_id: id,
          item_name: title,
          affiliation: url_domain,
          item_brand: brand?.name || '',
          ...itemCategories,
          item_list_id: id,
          item_list_name: title,
          price: price_amount,
        },
      ],
    };
  });

  return (
    <>
      <div className="ecom-detail">
        <Breadcrumbs firstHasPreIcon breadcrumbs={breadcrumbs} />
        <div className="ecom-detail__main">
          <div>
            {images && <ImageGallery images={images} />}
            {!isMobile && <div className="ecom-detail__attributes">{renderAttributes()}</div>}
          </div>

          <div className="ecom-detail__info">
            <div className="ecom-detail__chips">
              {labels &&
                labels.map((chip, index) => (
                  <Chip name={chip.name} color={'amber'} type={'filled'} iconName={'circle-check'} key={index} />
                ))}
            </div>

            <a href={vendor?.url} className="ecom-detail__info-brand basic-14">
              {vendor?.name}
            </a>
            <span className="ecom-detail__info-brand basic-14" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
              |
            </span>
            <a href={brand?.url} className="ecom-detail__info-brand basic-14">
              {brand?.name}
            </a>
            <h4 className="ecom-detail__info-title">{title}</h4>
            <p className="ecom-detail__info-description basic-16">{content}</p>
            <h5 className="ecom-detail__info-price">{formattedPrice}</h5>

            <div className="ecom-detail__info-button-container">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="button button--primary button--large "
                onClick={() => {
                  handleGoToClick();
                }}
              >
                <span>
                  {translations?.ecommerce?.goTo || 'Go to'} {url_domain ? url_domain : getDomainFromUrl(url)}
                </span>
                <Icon name="external-link" size="small" />
              </a>
            </div>
          </div>
        </div>

        {isMobile && <div className="ecom-detail__attributes">{renderAttributes()}</div>}

        <div className="ecom-detail__recommendations">
          {recommendedProducts && (
            <ProductsBlock
              products={recommendedProducts}
              sectionHeader={{
                title: translations?.ecommerce?.youMayAlsoLike || 'You may also like',
              }}
              mobileView="swiper"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default DetailPage;
